import * as React from "react";
import { useState, useEffect } from "react";
import { MfaServices } from "@/services/bedrock/MfaServices";
import { useSocialCoachSession } from "@/utils";
import { Button } from "@/components/ui/button";
import { Svg } from "@/assets";
import { toast } from "sonner";
import { Smartphone, Fingerprint, ArrowRight, RefreshCcw, Loader2 } from "lucide-react";
import { SmsSetupDialog } from "../SmsSetupDialog";
import { formatUsPhone } from "@/utils/mfaHelpers";

export const SecurityTab: React.FC = () => {
  const { session, isAdmin } = useSocialCoachSession();
  const [isSmsDialogOpen, setIsSmsDialogOpen] = useState(false);
  const [isRegisteringPasskey, setIsRegisteringPasskey] = useState(false);
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);

  // Check for passkeys on load
  useEffect(() => {
    MfaServices.getAuthChallenge()
      .then(options => setHasPasskey(!!options.allowCredentials?.length))
      .catch(() => setHasPasskey(false));
  }, []);

  const bufferToBase64 = (buffer: ArrayBuffer): string => {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  };

  const base64ToBuffer = (base64: string): ArrayBuffer => {
    const binaryString = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const handleRegisterPasskey = async () => {
    setIsRegisteringPasskey(true);
    try {
      const options = await MfaServices.getRegistrationChallenge();

      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge: base64ToBuffer(options.challenge),
        rp: options.rp,
        user: {
          ...options.user,
          id: base64ToBuffer(options.user.id),
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout,
        excludeCredentials: options.excludeCredentials?.map(cred => ({
          ...cred,
          id: base64ToBuffer(cred.id),
        })),
        authenticatorSelection: options.authenticatorSelection,
        attestation: options.attestation as AttestationConveyancePreference,
      };

      const credential = (await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      })) as PublicKeyCredential;

      if (!credential) {
        throw new Error("Failed to create credential");
      }

      const response = credential.response as AuthenticatorAttestationResponse;
      const payload = {
        id: credential.id,
        rawId: bufferToBase64(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bufferToBase64(response.attestationObject),
          clientDataJSON: bufferToBase64(response.clientDataJSON),
        },
        origin: window.location.origin,
      };

      await MfaServices.verifyRegistration(payload, "My Passkey");
      setHasPasskey(true);
      toast.success("Passkey registered successfully");
    } catch (e) {
      toast.error("Failed to register passkey");
      console.error(e);
    } finally {
      setIsRegisteringPasskey(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h3 className="text-xl font-bold text-slate-900">Security & Sign-In Methods</h3>
        <p className="text-sm text-slate-500 leading-relaxed">
          Keep your account secure by choosing how you verify your identity when signing in.
        </p>
      </div>

      <div className="grid gap-4">
        {/* SMS MFA Option */}
        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-5 hover:border-slate-300 hover:shadow-xs transition-all">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div className="flex gap-4 items-start">
              <div className="h-12 w-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                <Smartphone className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-semibold text-slate-800 text-[15px]">Text Message (SMS)</div>
                  {session?.mfaPhone?.verifiedAt ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200/60">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400"></span>
                      Off
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xl">
                  {session?.mfaPhone?.verifiedAt ? (
                    <>
                      We'll send a quick security code to{" "}
                      <span className="font-semibold text-slate-800">
                        {formatUsPhone(session.mfaPhone.phoneNumber)}
                      </span>{" "}
                      to make sure it's really you logging in.
                    </>
                  ) : (
                    "Receive a 6-digit code via text message when you log in from a new device. A quick, trusted backup to keep your account safe."
                  )}
                </p>
              </div>
            </div>
            <div className="shrink-0 flex items-center self-end md:self-center">
              {session?.mfaPhone?.verifiedAt ? (
                <Button
                  variant="outline"
                  onClick={() => setIsSmsDialogOpen(true)}
                  className="bg-white hover:bg-slate-50 border-slate-200 text-slate-700 hover:text-slate-800 gap-2 font-medium shadow-none h-10 px-4 rounded-lg"
                >
                  <RefreshCcw className="h-4 w-4 text-slate-500" />
                  <span>Change Number</span>
                </Button>
              ) : (
                <Button
                  onClick={() => setIsSmsDialogOpen(true)}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-sm h-10 px-4 rounded-lg gap-1.5"
                >
                  <span>Set Up SMS</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Passkeys & Biometrics Option */}
        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-5 hover:border-slate-300 hover:shadow-xs transition-all">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div className="flex gap-4 items-start">
              <div className="h-12 w-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                <Fingerprint className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-semibold text-slate-800 text-[15px]">Face ID, Fingerprint, or Screen Lock</div>
                  {hasPasskey === null ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                      <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                      Checking...
                    </span>
                  ) : hasPasskey ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200/60">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400"></span>
                      Off
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xl">
                  Log in instantly without typing passwords or waiting for text codes. Use your device's built-in facial
                  recognition, fingerprint sensor, or device PIN.
                </p>
              </div>
            </div>
            <div className="shrink-0 flex items-center self-end md:self-center">
              <Button
                variant={hasPasskey === null || hasPasskey ? "outline" : "default"}
                className={
                  hasPasskey === null
                    ? "bg-slate-50 border-slate-200 text-slate-400 gap-1.5 font-medium shadow-none h-10 px-4 rounded-lg cursor-not-allowed"
                    : hasPasskey
                    ? "bg-white hover:bg-slate-50 border-slate-200 text-slate-700 hover:text-slate-800 gap-1.5 font-medium shadow-none h-10 px-4 rounded-lg"
                    : "bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-sm h-10 px-4 rounded-lg gap-1.5"
                }
                onClick={handleRegisterPasskey}
                disabled={hasPasskey === null || isRegisteringPasskey}
              >
                {hasPasskey === null ? (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                ) : (
                  <Fingerprint className="h-4 w-4 shrink-0" />
                )}
                <span>
                  {hasPasskey === null
                    ? "Checking..."
                    : isRegisteringPasskey
                    ? "Registering..."
                    : hasPasskey
                    ? "Register Another"
                    : "Enable Passkey"}
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* Slack MFA Option (Admins Only) */}
        {isAdmin && (
          <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-5 hover:border-slate-300 hover:shadow-xs transition-all">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div className="flex gap-4 items-start">
                <div className="h-12 w-12 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center shrink-0 p-2.5">
                  <img src={Svg.SlackSVG} className="h-full w-full object-contain" alt="Slack Logo" />
                </div>
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-semibold text-slate-800 text-[15px]">Slack Notification Codes</div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      Active
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed max-w-xl">
                    Get your multi-factor verification codes sent straight to your Slack account. An incredibly fast and
                    convenient way to log in if you use Slack all day long.
                  </p>
                </div>
              </div>
              <div className="shrink-0 flex items-center self-end md:self-center gap-3">
                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-full select-none">
                  Connected
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <SmsSetupDialog open={isSmsDialogOpen} onOpenChange={setIsSmsDialogOpen} onSuccess={() => {}} />
    </div>
  );
};
